import config from 'temp/config';
import {
  GraphQLErrorPagesService,
  SitecoreContext,
  ComponentPropsContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { SitecorePageProps } from 'lib/page-props';
import { componentBuilder } from 'temp/componentBuilder';
import Layout from 'src/Layout';
import { GetStaticProps } from 'next';
import { siteResolver } from 'lib/site-resolver';
import clientFactory from 'lib/graphql-client-factory';
import { useEffect, useState } from 'react';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import Head from 'next/head';

/**
 * Rendered in case if we have 500 error
 */
export const DefaultServerError = (): JSX.Element => (
  <>
    <Head>
      <title>500: Server Error</title>
    </Head>
    <div style={{ padding: 10 }}>
      <h1>500 Internal Server Error</h1>
      <p>There is a problem with the resource you are looking for, and it cannot be displayed.</p>
      <a href="/">Go to the Home page</a>
    </div>
  </>
);

type ErrorPageMap = Record<string, SitecorePageProps | null>;
const Custom500 = (props: SitecorePageProps & { resultErrorPages: ErrorPageMap }): JSX.Element => {
  const [pageProps, setPageProps] = useState<SitecorePageProps | null | 'loading'>('loading');

  // We need to fetch determine the site name client-side because NextJS doesn't provide the ability
  // to determine which site we're on due to middleware rewrites not executing for the 404 handler.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const domain = window.location.hostname;
    const siteName = siteResolver.getByHost(domain ?? '')?.name ?? config.sitecoreSiteName;

    const errorPages = props?.resultErrorPages?.[siteName ?? ''] ?? null;

    setPageProps(errorPages);
  }, [props?.resultErrorPages]);

  if (pageProps === 'loading') {
    return <></>;
  }
  if (pageProps === null) {
    return <DefaultServerError />;
  }
  return (
    <ComponentPropsContext value={pageProps.componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory()}
        layoutData={pageProps.layoutData}
      >
        <Layout layoutData={pageProps.layoutData} headLinks={props.headLinks} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const resultErrorPages: ErrorPageMap = {};
  for (let index = 0; index < siteResolver.sites.length; index++) {
    const site = siteResolver.sites[index];

    const errorPagesService = new GraphQLErrorPagesService({
      clientFactory,
      siteName: site.name,
      language: context.locale || config.defaultLanguage,
      retries:
        (process.env.GRAPH_QL_SERVICE_RETRIES &&
          parseInt(process.env.GRAPH_QL_SERVICE_RETRIES, 10)) ||
        0,
    });

    if (process.env.DISABLE_SSG_FETCH?.toLowerCase() !== 'true') {
      try {
        const errorPages = await errorPagesService.fetchErrorPages();

        context.params = {
          ...context.params,
          path: errorPages?.serverErrorPagePath.split('/'),
        };

        const props = await sitecorePagePropsFactory.create(context);

        resultErrorPages[site.name] = props;
      } catch (error) {
        console.error('Error occurred while fetching error pages');
        console.error(error);
      }
    }
  }
  return {
    props: {
      headLinks: [],
      resultErrorPages,
    },
  };
};

export default Custom500;
